import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import IdentityContext from '../../../contexts/IdentityContext';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { getSlideSets, SlideSet } from '../_api/SlideSetApi';
import ModuleContext from './ModuleContext';
import TableOfContentsContext from './TableOfContentsContext';

type SlideSetsContextValue = {
  slideSets: SlideSet[];
  setSlideSets: React.Dispatch<React.SetStateAction<SlideSet[]>>;
  fetchSlideSets: () => void;
};

const SlideSetsContext = createContext<SlideSetsContextValue>({
  slideSets: [],
  setSlideSets: () => undefined,
  fetchSlideSets: () => undefined,
});

export default SlideSetsContext;

export function SlideSetsContextProvider({ children }: PropsWithChildren) {
  const handleError = useThrowAsyncError();

  const { identity } = useContext(IdentityContext);
  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);

  const [slideSets, setSlideSets] = useState<SlideSet[]>([]);

  const fetchSlideSets = useCallback(async () => {
    if (!selectedModule || !selectedNode || !identity.isTeacherAlike) return;

    try {
      const data = await getSlideSets(selectedModule.id, selectedNode.id);

      setSlideSets(data);
    } catch (e) {
      handleError(e);
    }
  }, [handleError, identity.isTeacherAlike, selectedModule, selectedNode]);

  useEffect(() => {
    fetchSlideSets();
  }, [fetchSlideSets]);

  return (
    <SlideSetsContext.Provider value={{ slideSets, setSlideSets, fetchSlideSets }}>
      {children}
    </SlideSetsContext.Provider>
  );
}
